import { useMemo } from "react";
import { t } from "@/i18n-js/instance";
import { useFetchProration } from "@/react/components/shared/UpgradePlan/hooks/useFetchProration";
import type { Plan } from "@circle-react/types";
import { I18N_LOCALE_ROOT } from "../constants";

export interface CalculatedPricing {
  annualSavings?: string;
  currentPlanCredit?: string;
  discountCoupon?: string;
  dueToday?: string;
  isAnnualPlan?: boolean;
}

interface ProrationData {
  display_prorated_charges_credit_amount?: string;
  display_total_discount?: string;
  display_total_due_today?: string;
}

interface UsePlanPricingProps {
  alternativePlan?: Plan;
  isAnnualPlan: boolean;
  isCommunityOnTrial: boolean;
  isPlanLoading: boolean;
  plan?: Plan;
  planCouponId?: string;
}

const HARDCODED_DISCOUNT_PERCENTAGE = 25;

const extractProrationPricing = (
  prorationData: ProrationData,
): CalculatedPricing => {
  const result: CalculatedPricing = {};

  if (prorationData.display_prorated_charges_credit_amount) {
    result.currentPlanCredit =
      prorationData.display_prorated_charges_credit_amount;
  }

  if (prorationData.display_total_discount) {
    result.discountCoupon = prorationData.display_total_discount;
  }

  if (prorationData.display_total_due_today) {
    result.dueToday = prorationData.display_total_due_today;
  }

  return result;
};

const calculateDueTodayAmount = (plan: Plan, isAnnualPlan: boolean): string => {
  if (plan.amount && typeof plan.amount === "number") {
    const currency = plan.currency?.toUpperCase() || "USD";
    return `$${(plan.amount / 100).toFixed(0)} ${currency}`;
  }

  if (isAnnualPlan) {
    return plan.annual_base_amount_with_currency || "";
  }

  return plan.display_base_monthly_amount || "";
};

const parsePrice = (
  priceString: string,
): { amount: number; currency: string } => {
  const parts = priceString.split(" ");
  const currency = parts.length > 1 ? parts[parts.length - 1] : "USD";
  const numericPart = parts[0].replace(/[$,]/g, "");
  const amount = parseFloat(numericPart);

  return { amount: isNaN(amount) ? 0 : amount, currency };
};

const formatPrice = (amount: number, currency: string): string => {
  const rounded = Math.round(amount);
  return `$${rounded.toLocaleString("en-US")} ${currency}`;
};

const calculateDiscountAmount = (
  priceString: string,
  discountPercentage: number,
): { discountAmount: string; finalAmount: string } => {
  const { amount, currency } = parsePrice(priceString);

  if (amount === 0) {
    return { discountAmount: "", finalAmount: priceString };
  }

  const discount = Math.round((amount * discountPercentage) / 100);
  const final = amount - discount;

  return {
    discountAmount: `-${formatPrice(discount, currency)}`,
    finalAmount: formatPrice(final, currency),
  };
};

const applyDiscountToDueToday = (
  currentDueToday: string,
  discountAmount: string,
): string => {
  const { amount: currentAmount, currency } = parsePrice(currentDueToday);
  const { amount: discountValue } = parsePrice(discountAmount.replace("-", ""));

  const finalAmount = currentAmount - discountValue;
  return formatPrice(finalAmount, currency);
};

export const usePlanPricing = ({
  plan,
  alternativePlan,
  isAnnualPlan,
  isCommunityOnTrial,
  planCouponId,
  isPlanLoading,
}: UsePlanPricingProps) => {
  // Only fetch proration data if the community is not on trial
  const shouldFetchProration = !isCommunityOnTrial && !!plan?.id;

  const { prorationData, isProrationLoading, errorFetchingProration } =
    useFetchProration({
      new_plan_id: plan?.id || 0,
      plan_coupon_id: planCouponId,
      only_ch: true,
      enabled: shouldFetchProration,
    });

  const pricing = useMemo((): CalculatedPricing => {
    if (!plan) return {};

    let calculatedPricing: CalculatedPricing = {};

    // Use proration data if available
    if (prorationData && !errorFetchingProration) {
      calculatedPricing = extractProrationPricing(prorationData);
    }

    // Always include annual savings (regardless of interval)
    // Use the annual plan's discount amount if we're on annual, otherwise use the alternative plan's discount
    if (!calculatedPricing.annualSavings) {
      const annualPlan = isAnnualPlan ? plan : alternativePlan;
      if (annualPlan?.annual_discount_amount_with_currency) {
        const currency = plan.currency.toUpperCase();
        calculatedPricing.annualSavings = t(
          `${I18N_LOCALE_ROOT}.pricing_summary.discount_format`,
          {
            amount: annualPlan.annual_discount_amount_with_currency,
            currency: currency,
          },
        );
      }
    }

    // Calculate due today if not set by proration
    if (!calculatedPricing.dueToday) {
      calculatedPricing.dueToday = calculateDueTodayAmount(plan, isAnnualPlan);
    }

    // HARDCODED: Apply 25% discount if plan is on offer
    // The discount is calculated on the ORIGINAL plan price (before credit), like Stripe does
    if (plan.is_on_offer) {
      // Get the original plan price (before any credits)
      const originalPrice = calculateDueTodayAmount(plan, isAnnualPlan);

      // Calculate 25% discount on the original price
      const { discountAmount, finalAmount } = calculateDiscountAmount(
        originalPrice,
        HARDCODED_DISCOUNT_PERCENTAGE,
      );

      // Store the discount amount
      calculatedPricing.discountCoupon = discountAmount;

      // If we have proration data with dueToday, subtract the discount from it
      // Otherwise, use the calculated final amount
      if (
        calculatedPricing.dueToday &&
        prorationData?.display_total_due_today
      ) {
        calculatedPricing.dueToday = applyDiscountToDueToday(
          calculatedPricing.dueToday,
          discountAmount,
        );
      } else {
        calculatedPricing.dueToday = finalAmount;
      }
    }

    // Add the isAnnualPlan flag to the pricing object
    calculatedPricing.isAnnualPlan = isAnnualPlan;

    return calculatedPricing;
  }, [
    plan,
    alternativePlan,
    isAnnualPlan,
    prorationData,
    errorFetchingProration,
  ]);

  // Only consider proration loading if we're actually fetching proration
  const isLoadingPricing = !!(
    isPlanLoading ||
    (shouldFetchProration && isProrationLoading)
  );

  return {
    pricing,
    isLoadingPricing,
  };
};
